import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { ALL_ACHIEVEMENTS } from "@/lib/achievementConfig";

interface SubTask {
  id: string;
  title: string;
  duration?: string;
  required?: boolean;
  stage_context?: string;
  milestone?: string;
  milestone_badge?: string;
  attachment?: {
    title: string;
    type: string;
    url: string;
  };
}

interface Task {
  id: string;
  task_number: number;
  task_title: string;
  task_description: string;
  sub_tasks: SubTask[];
}

interface StageTaskChecklistProps {
  stageType: string;
}

export const StageTaskChecklist = ({ stageType }: StageTaskChecklistProps) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [progress, setProgress] = useState<Record<string, Record<string, boolean>>>({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchStageTasks();
  }, [stageType, user]);

  const fetchStageTasks = async () => {
    if (!user) return;

    try {
      // Fetch all tasks that have sub-tasks with this stage_context
      const { data: tasksData, error: tasksError } = await supabase
        .from("standardized_tasks")
        .select("id, task_number, task_title, task_description, sub_tasks")
        .not("sub_tasks", "is", null);

      if (tasksError) throw tasksError;

      // Filter tasks that have sub-tasks for this stage
      const relevantTasks = (tasksData || [])
        .map(task => {
          const subTasks = Array.isArray(task.sub_tasks) ? task.sub_tasks as unknown as SubTask[] : [];
          return {
            ...task,
            sub_tasks: subTasks.filter(st => st.stage_context === stageType)
          };
        })
        .filter(task => task.sub_tasks.length > 0);

      setTasks(relevantTasks);

      // Fetch user progress for these tasks
      if (relevantTasks.length > 0) {
        const taskIds = relevantTasks.map(t => t.id);
        const { data: progressData, error: progressError } = await supabase
          .from("user_task_progress")
          .select("task_id, sub_task_progress")
          .eq("user_id", user.id)
          .in("task_id", taskIds);

        if (progressError) throw progressError;

        const progressMap: Record<string, Record<string, boolean>> = {};
        (progressData || []).forEach(p => {
          progressMap[p.task_id] = (p.sub_task_progress as Record<string, boolean>) || {};
        });
        setProgress(progressMap);
      }
    } catch (error: any) {
      console.error("Error fetching stage tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubTask = async (taskId: string, subTaskId: string, currentValue: boolean) => {
    if (!user) return;

    const newValue = !currentValue;

    try {
      // Get current progress for this task
      const { data: existingProgress } = await supabase
        .from("user_task_progress")
        .select("*")
        .eq("user_id", user.id)
        .eq("task_id", taskId)
        .single();

      const currentProgress = (existingProgress?.sub_task_progress as Record<string, boolean>) || {};
      const updatedSubTaskProgress = {
        ...currentProgress,
        [subTaskId]: newValue,
      };

      if (existingProgress) {
        // Update existing progress
        const { error } = await supabase
          .from("user_task_progress")
          .update({
            sub_task_progress: updatedSubTaskProgress,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingProgress.id);

        if (error) throw error;
      } else {
        // Create new progress record
        const { error } = await supabase
          .from("user_task_progress")
          .insert({
            user_id: user.id,
            task_id: taskId,
            status: "in_progress",
            sub_task_progress: updatedSubTaskProgress,
          });

        if (error) throw error;
      }

      // Update local state
      setProgress(prev => ({
        ...prev,
        [taskId]: updatedSubTaskProgress,
      }));

      // Check if this sub-task has a milestone
      const task = tasks.find(t => t.id === taskId);
      const subTask = task?.sub_tasks.find(st => st.id === subTaskId);
      
      if (newValue && subTask?.milestone) {
        // Show confetti and toast for milestone achievement
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });

        toast({
          title: "Achievement Unlocked! 🎉",
          description: subTask.milestone,
          duration: 5000,
        });

              // Record achievement in database
              const achievementId = subTask.milestone_badge || `task_${taskId}_${subTaskId}`;
              const achievement = ALL_ACHIEVEMENTS.find(a => a.id === achievementId);

              await supabase.from("user_achievements").insert({
                user_id: user.id,
                achievement_id: achievementId,
                achievement_name: achievement?.name || "Task Achievement",
                description: achievement?.description || subTask.milestone,
                metadata: { task_id: taskId, sub_task_id: subTaskId },
              });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive",
      });
      console.error("Error updating sub-task:", error);
    }
  };

  if (loading) {
    return null;
  }

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-primary" />
        <h2 className="text-2xl font-bold">Your Action Items</h2>
      </div>
      <p className="text-muted-foreground mb-6">
        Complete these user-driven tasks as part of your journey.
      </p>

      {tasks.map((task) => {
        const taskProgress = progress[task.id] || {};
        const completedCount = task.sub_tasks.filter(st => taskProgress[st.id]).length;
        const totalCount = task.sub_tasks.length;

        return (
          <Card key={task.id} className="border-2">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">
                    {task.task_title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {task.task_description}
                  </p>
                </div>
                <Badge variant="secondary">
                  {completedCount}/{totalCount} Complete
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {task.sub_tasks.map((subTask) => {
                const isComplete = taskProgress[subTask.id] || false;
                
                return (
                  <div
                    key={subTask.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      isComplete ? "bg-muted/50 border-primary/50" : "hover:bg-muted/30"
                    }`}
                  >
                    <button
                      onClick={() => handleToggleSubTask(task.id, subTask.id, isComplete)}
                      className="mt-0.5 flex-shrink-0"
                    >
                      {isComplete ? (
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className={`text-sm font-medium ${
                            isComplete ? "line-through text-muted-foreground" : ""
                          }`}
                        >
                          {subTask.title}
                        </span>
                      </div>
                      {subTask.milestone && (
                        <p className="text-xs text-primary mt-1 font-medium">
                          {subTask.milestone}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
